import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, RefreshCw } from 'lucide-react';
import { disputesService } from '../services/disputes.service';
import { useDialog } from '../contexts/DialogContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

const age = (iso) => {
    if (!iso) return '—';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
};

const STATUS_BADGE = {
    open: 'bg-red-100 text-red-700',
    under_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-gray-100 text-gray-600',
};

const DisputesQueue = () => {
    const navigate = useNavigate();
    const { toast, prompt } = useDialog();
    const [status, setStatus] = useState('open');
    const [rows, setRows] = useState([]);
    const [openCount, setOpenCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await disputesService.list(status);
            setRows(Array.isArray(res?.items) ? res.items : []);
            setOpenCount(res?.open_count ?? 0);
        } catch (e) {
            toast.error(e.message || 'Failed to load disputes');
        } finally {
            setLoading(false);
        }
    }, [status, toast]);

    useEffect(() => {
        setLoading(true);
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const startReview = async (id) => {
        try {
            await disputesService.update(id, { status: 'under_review' });
            toast.success('Marked under review');
            await load();
        } catch (e) {
            toast.error(e.message || 'Failed');
        }
    };

    const approve = async (d) => {
        const values = await prompt({
            title: `Approve dispute — service ${d.service_number || `#${d.service_id}`}`,
            confirmLabel: 'Approve',
            fields: [
                { key: 'resolution_note', label: 'Resolution note', required: true, multiline: true },
                {
                    key: 'waive_fee',
                    label: 'Waive cancellation fee (optional, $) — leave blank for none. Full refund: use the Payment panel instead.',
                    type: 'number',
                    placeholder: 'e.g. 25.00',
                },
            ],
        });
        if (!values) return;
        const body = { status: 'approved', resolution_note: values.resolution_note };
        const waive = parseFloat(values.waive_fee);
        if (!isNaN(waive) && waive > 0) {
            body.issue_refund = { type: 'fee_waiver', amount: waive };
        }
        try {
            await disputesService.update(d.id, body);
            toast.success(body.issue_refund ? 'Approved and fee waived' : 'Approved');
            await load();
        } catch (e) {
            toast.error(e.message || 'Approve failed');
        }
    };

    const reject = async (d) => {
        const values = await prompt({
            title: `Reject dispute — service ${d.service_number || `#${d.service_id}`}`,
            confirmLabel: 'Reject',
            danger: true,
            fields: [{ key: 'resolution_note', label: 'Resolution note', required: true, multiline: true }],
        });
        if (!values) return;
        try {
            await disputesService.update(d.id, { status: 'rejected', resolution_note: values.resolution_note });
            toast.success('Rejected');
            await load();
        } catch (e) {
            toast.error(e.message || 'Reject failed');
        }
    };

    return (
        <div className="p-4 h-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Scale className="w-5 h-5 text-red-500" />
                    <h1 className="text-xl font-semibold">Disputes</h1>
                    <span className="text-xs text-gray-500">{openCount} open</span>
                </div>
                <button type="button" className="px-3 py-1.5 border rounded text-sm" onClick={load}>
                    <RefreshCw className="w-4 h-4 inline mr-1" />Refresh
                </button>
            </div>

            <div className="flex gap-3 mb-3 text-sm">
                <select className="border rounded px-2 py-1" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="open">Open</option>
                    <option value="under_review">Under review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="all">All</option>
                </select>
            </div>

            <div className="border rounded bg-white overflow-auto">
                {loading && <div className="p-8 flex justify-center"><LoadingSpinner /></div>}
                {!loading && (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-500 bg-gray-50">
                            <tr>
                                <th className="text-left px-3 py-2">Service</th>
                                <th className="text-left px-3 py-2">Reason</th>
                                <th className="text-left px-3 py-2">Requested outcome</th>
                                <th className="text-right px-3 py-2">Raised</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => (
                                <tr key={d.id} className={`border-t ${d.status === 'open' ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                                    <td className="px-3 py-2">
                                        <button
                                            type="button"
                                            className="text-blue-600 hover:underline"
                                            onClick={() => navigate(`/add-service?service_id=${d.service_id}${d.case_id ? `&case_id=${d.case_id}` : ''}`)}
                                        >
                                            {d.service_number || `#${d.service_id}`}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2">
                                        {d.reason_code}
                                        {d.reason_text ? <div className="text-xs text-gray-500">{d.reason_text}</div> : null}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">{d.requested_outcome || '—'}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{age(d.created_at)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[d.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {d.status}
                                        </span>
                                        {d.resolution_note ? (
                                            <div className="text-xs text-gray-500 mt-0.5">{d.resolution_note}</div>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {(d.status === 'open' || d.status === 'under_review') && (
                                            <>
                                                {d.status === 'open' && (
                                                    <button
                                                        type="button"
                                                        className="text-xs text-amber-700 hover:underline mr-3"
                                                        onClick={() => startReview(d.id)}
                                                    >
                                                        Start review
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="text-xs text-green-700 hover:underline mr-3"
                                                    onClick={() => approve(d)}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-red-600 hover:underline"
                                                    onClick={() => reject(d)}
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                                        {status === 'open' ? 'No open disputes.' : 'Nothing here.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DisputesQueue;
